"use client";

import IconUser from "@public/images/noti/User.svg";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { NotifyType, useGetNotificationList } from "@/api/notification/getNotify";
import { useGetUnreadCountNotify } from "@/api/notification/getUnreadCountNotify";
import { usePatchReadNotify } from "@/api/notification/patchReadNotify";
import { usePostAllReadNotify } from "@/api/notification/postAllReadNotify";
import { NotiCard } from "@/components/ui/notification/NotiCard";
import {
  NOTIFICATION_TYPE_CONFIG,
  NotificationType,
} from "@/components/ui/notification/notification.type";
import { TypoGraph } from "@/components/ui/primitives";
import { BellIcon, DropdownIcon, DropupIcon } from "@/components/ui/primitives/icons";

const SidebarProfile = () => {
  const [username, setUsername] = useState<string | null>(null);
  const [isOpenNotification, setIsOpenNotification] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<NotificationType | "ALL">("ALL");
  const [scrollPosition, setScrollPosition] = useState(0);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [isOpenLabelMenu, setIsOpenLabelMenu] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedUsername = localStorage.getItem("username");
      setUsername(storedUsername);
    }
  }, []);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchNotifications,
  } = useGetNotificationList(
    selectedCategory === "ALL" ? undefined : (selectedCategory as string)
  );
  const { mutate: patchReadNotify } = usePatchReadNotify();
  const { mutate: postAllReadNotify } = usePostAllReadNotify();
  const { data: unreadCountData, refetch: refetchUnreadCount } =
    useGetUnreadCountNotify();

  const notiListRaw = data?.pages.flatMap((page) => page.results) || [];

  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        setScrollPosition(scrollTop);
      }
    };

    const container = scrollContainerRef.current;
    if (container && isOpenNotification) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [isOpenNotification]);

  useEffect(() => {
    if (isOpenNotification && scrollPosition <= 50 && hasNewNotifications) {
      refetchNotifications();
      setHasNewNotifications(false);
    }
  }, [scrollPosition, hasNewNotifications, isOpenNotification, refetchNotifications]);

  const handleSSENotification = (newNotification: NotifyType) => {
    refetchUnreadCount();

    if (!isOpenNotification) {
      refetchNotifications();
    } else {
      if (scrollPosition <= 50) {
        refetchNotifications();
        queryClient.invalidateQueries({
          queryKey: ["notifications", undefined, 10],
          exact: true,
        });
      } else {
        setHasNewNotifications(true);
      }
    }
  };

  useEffect(() => {
    // SSE connection placeholder
  }, []);

  // 무한 스크롤
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loadMoreRef.current && isOpenNotification) {
        const observer = new IntersectionObserver(
          (entries) => {
            const target = entries[0];

            if (
              target.isIntersecting &&
              hasNextPage &&
              !isFetchingNextPage &&
              notiListRaw.length > 0
            ) {
              fetchNextPage();
            }
          },
          { threshold: 1.0 }
        );

        observer.observe(loadMoreRef.current);

        return () => {
          observer.disconnect();
        };
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    isOpenNotification,
    selectedCategory,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    notiListRaw.length,
  ]);

  // 라벨 메뉴 외부 클릭 닫기
  useEffect(() => {
    if (!isOpenLabelMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpenLabelMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpenLabelMenu]);

  const handleNotificationClick = () => {
    setIsOpenNotification((prev) => !prev);
  };

  const closeNotification = () => {
    if (hasNewNotifications) {
      refetchNotifications();
      setHasNewNotifications(false);
    }
    setIsOpenNotification(false);
  };

  const handleCategoryChange = (category: NotificationType | "ALL") => {
    setSelectedCategory(category);
    setIsOpenLabelMenu(false);

    if (hasNewNotifications) {
      refetchNotifications();
      setHasNewNotifications(false);
    }
  };

  const isReadAllDisabled = () => {
    if (isLoading) return true;
    if (notiListRaw.length === 0) return true;

    if (selectedCategory === "ALL") {
      return !unreadCountData?.count || unreadCountData.count === 0;
    } else {
      const unreadInCategory = notiListRaw.filter((noti) => !noti.isRead);
      return unreadInCategory.length === 0;
    }
  };

  const handleMarkAllAsRead = () => {
    if (isReadAllDisabled()) return;

    if (selectedCategory === "ALL") {
      postAllReadNotify({});
    } else {
      postAllReadNotify({ category: selectedCategory });
    }
  };

  const getSelectedLabel = () => {
    if (selectedCategory === "ALL") return "모든 알림";
    return NOTIFICATION_TYPE_CONFIG[selectedCategory]?.title || "모든 알림";
  };

  const handleNotiClick = (noti: NotifyType) => {
    if (typeof window === "undefined") return;

    window.location.href = noti.link;
    patchReadNotify(noti.id);
  };

  const disabled = isReadAllDisabled();

  const renderNotificationContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center w-full flex-1">
          <TypoGraph variant="headline04" className="text-gray-700">
            로딩 중
          </TypoGraph>
        </div>
      );
    }

    if (notiListRaw.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center w-full flex-1">
          <TypoGraph variant="headline04" className="text-gray-700">
            받은 알림이 없습니다.
          </TypoGraph>
        </div>
      );
    }

    return (
      <>
        {notiListRaw.map((notification) => (
          <NotiCard
            key={notification.id}
            notification={notification}
            onClick={handleNotiClick}
          />
        ))}

        <div ref={loadMoreRef} className="h-px" />

        {isFetchingNextPage && (
          <div className="flex items-center justify-center w-full h-10">
            <TypoGraph variant="body01" className="text-gray-600">
              로딩 중
            </TypoGraph>
          </div>
        )}
      </>
    );
  };

  const renderNotificationDrawer = () => {
    if (!isOpenNotification) return null;

    return createPortal(
      <>
        {/* Overlay */}
        <div
          className="fixed top-(--header-h) left-0 w-screen h-[calc(100vh-var(--header-h))] bg-transparent z-[999]"
          onClick={closeNotification}
        />
        {/* Drawer */}
        <div className="fixed top-(--header-h) left-[200px] w-[393px] h-[calc(100vh-var(--header-h))] bg-white z-[1001] shadow-depth200 backdrop-blur-[16px] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center px-5 pt-8 pb-4">
            <div ref={menuRef} className="relative">
              <button
                className="flex items-center gap-2 h-[26px] cursor-pointer bg-white text-black hover:bg-white focus:bg-white active:bg-white border-none outline-none"
                onClick={() => setIsOpenLabelMenu((prev) => !prev)}
              >
                <TypoGraph variant="headline03">{getSelectedLabel()}</TypoGraph>
                {isOpenLabelMenu ? <DropupIcon /> : <DropdownIcon />}
              </button>

              {isOpenLabelMenu && (
                <div className="absolute top-full left-0 mt-1 min-w-[160px] rounded-lg shadow-depth200 bg-white p-1 z-10">
                  <div
                    className="px-3 py-1.5 cursor-pointer hover:bg-gray-200 rounded text-left max-w-[152px]"
                    onClick={() => handleCategoryChange("ALL")}
                  >
                    <TypoGraph variant="headline03">모든 알림</TypoGraph>
                  </div>

                  {Object.entries(NOTIFICATION_TYPE_CONFIG).map(([key, config]) => (
                    <div
                      key={key}
                      className="px-3 py-1.5 cursor-pointer hover:bg-gray-200 rounded text-left max-w-[152px]"
                      onClick={() => handleCategoryChange(key as NotificationType)}
                    >
                      <TypoGraph variant="headline03">{config.title}</TypoGraph>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              className={`min-w-[72px] h-7 rounded-4 px-2 cursor-pointer border-none ${
                disabled
                  ? "bg-gray-200 cursor-not-allowed"
                  : "bg-primary-100 hover:bg-primary-200"
              }`}
              onClick={handleMarkAllAsRead}
              disabled={disabled}
            >
              <TypoGraph
                variant="label05"
                className={disabled ? "text-gray-700" : "text-primary-700"}
              >
                전체 읽음
              </TypoGraph>
            </button>
          </div>

          {/* Body */}
          <div
            ref={scrollContainerRef}
            className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-4 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-500/75 [&::-webkit-scrollbar-thumb]:rounded-[10px] [&::-webkit-scrollbar-thumb]:border-4 [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-white [&::-webkit-scrollbar-thumb]:bg-clip-padding"
          >
            {renderNotificationContent()}
          </div>
        </div>
      </>,
      document.body
    );
  };

  return (
    <div className="flex w-full h-[60px] items-center justify-between p-2.5">
      <div className="flex items-center justify-between w-full p-2 rounded-lg transition-colors duration-200 hover:bg-primary-800">
        <div className="flex items-center min-w-0 gap-2 cursor-pointer flex-1">
          {/* Profile Image */}
          <div className="relative w-10 h-10 overflow-hidden flex items-center justify-center">
            <IconUser />
          </div>
          <div className="min-w-0 flex-1">
            <TypoGraph
              variant="label01"
              className="text-white truncate w-full font-semibold"
            >
              {username}
            </TypoGraph>
          </div>
        </div>
        {/* Notification */}
        <div className="relative">
          <button
            aria-label="noti-button"
            className="inline-flex items-center justify-center w-8 h-8 p-1 text-white bg-transparent border-none cursor-pointer hover:bg-transparent rounded-4"
            onClick={handleNotificationClick}
          >
            <BellIcon size={22} className="text-white" />
          </button>
          {unreadCountData?.count != null && unreadCountData.count > 0 && (
            <div className="absolute -top-px right-0 w-[18px] h-[18px] bg-danger-400 rounded-full flex items-center justify-center text-white text-[10px] font-semibold pointer-events-none">
              {unreadCountData.count > 99 ? "99" : unreadCountData.count}
            </div>
          )}
        </div>
      </div>

      {renderNotificationDrawer()}
    </div>
  );
};

export default SidebarProfile;
